import { useEffect, useRef, useState } from 'react';
import posts from '../data/generated/posts.json';
import ParticleField from './ParticleField';
import Grain from './Grain';
import useIsMobile from '../hooks/useIsMobile';
import { PALETTES } from '../theme';
import './Blog.css';
import BlogArticle from './BlogArticle';

export default function Blog({ path, theme = 'dark' }) {
    const slug = path.split('/')[2];
    const post = posts.find(item => `/blog/${item.slug}` === path);
    const mobile = useIsMobile();
    const scroller = useRef(null);
    const mobileContents = useRef(null);
    const [progress, setProgress] = useState(0);
    const [active, setActive] = useState('');
    const palette = PALETTES[theme] || PALETTES.dark;

    useEffect(() => {
        document.title = `${post ? post.title : slug ? 'Page not found' : 'Blog'} — Jonathan Edward`;
        const description = document.querySelector('meta[name="description"]');
        const previous = description?.content;
        if (description) description.content = post?.description || 'Project notes on software and home infrastructure.';
        return () => { if (description) description.content = previous; };
    }, [post, slug]);

    useEffect(() => {
        if (!post) return undefined;
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => { if (entry.isIntersecting) setActive(entry.target.id); });
        }, { root: scroller.current, rootMargin: '-5% 0px -75% 0px' });
        post.headings.forEach(({ id }) => {
            const element = document.getElementById(id);
            if (element) observer.observe(element);
        });
        if (window.location.hash) {
            try {
                const element = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
                element?.scrollIntoView();
            } catch { /* Ignore malformed anchor URLs. */ }
        }
        return () => observer.disconnect();
    }, [post]);

    const contents = post && <ol>{post.headings.map((heading, i) => <li key={heading.id}>
        <a href={`#${heading.id}`} aria-current={active === heading.id ? 'location' : undefined}
            onClick={() => { if (mobileContents.current) mobileContents.current.open = false; }}>
            <span>{String(i + 1).padStart(2, '0')}</span>{heading.title}
        </a>
    </li>)}</ol>;

    return <div className="sig-app blog-shell" style={{ '--accent': palette.accent, '--blog-bg': theme === 'light' ? '#e8e5df' : '#060708', color: palette.color, background: 'var(--blog-bg)' }}>
        <div className="blog-atmosphere"><ParticleField theme={theme} dimmed mobile={mobile} /><Grain /></div>
        <div className="blog-frame" />
        <div className="blog-scroll" ref={scroller} onScroll={event => {
            const element = event.currentTarget;
            setProgress(element.scrollTop / Math.max(1, element.scrollHeight - element.clientHeight));
        }}>
            <a className="blog-skip" href="#blog-main">Skip to content</a>
            <header className="blog-header">
                <a className="blog-wordmark" href="/">Jonathan Edward<span>Electrical Engineering / Software / Systems</span></a>
                <nav aria-label="Primary"><a href="/">Portfolio <span aria-hidden="true">↗</span></a><a href="/blog" aria-current="page" onClick={!slug ? event => event.preventDefault() : undefined}>Blog <span className="blog-dot">·</span></a></nav>
            </header>
            <main id="blog-main" className="blog-main">
                {!slug ? <>
                    <h1 className="blog-list-title">Blog</h1>
                    <div className="blog-posts">{posts.map(item => <a className="blog-post-card" key={item.slug} href={`/blog/${item.slug}`}>
                        <span className="blog-eyebrow">{item.tags[0]}</span>
                        <div><h2>{item.title}</h2><p>{item.description}</p></div>
                        <span className="blog-card-arrow" aria-hidden="true">↗</span>
                    </a>)}</div>
                </> : post ? <>
                    <a className="blog-back" href="/blog">← All posts</a>
                    <header className="blog-article-header">
                        <div className="blog-eyebrow">{post.tags[0]}</div>
                        <h1>{post.title}</h1>
                        <p className="blog-intro">{post.description}</p>
                        <div className="blog-meta">{post.date && <time dateTime={post.date}>{post.date}</time>}{post.draft && <span className="blog-draft">Work in progress</span>}</div>
                        <div className="blog-tags">{post.tags.map(tag => <span key={tag}>{tag}</span>)}</div>
                    </header>
                    <div className="blog-layout">
                        <aside className="blog-contents"><div className="blog-eyebrow">On this page</div><nav aria-label="Table of contents">{contents}</nav><a className="blog-back" href="#blog-main">↑ Back to top</a></aside>
                        <div className="blog-reading-column">
                            {post.draft && <p className="blog-editor-note">An evolving project journal. Details and screenshots are still being added.</p>}
                            {/* HTML is compiled from local Markdown with raw HTML disabled. */}
                            <BlogArticle html={post.html} theme={theme} />
                        </div>
                    </div>
                </> : <div className="blog-not-found"><div className="blog-eyebrow">404</div><h1>Page not found.</h1><a className="blog-back" href="/blog">← Back to the blog</a></div>}
            </main>
        </div>
        {post && <details ref={mobileContents} className="blog-mobile-contents" onKeyDown={event => {
            if (event.key === 'Escape') { event.currentTarget.open = false; event.currentTarget.querySelector('summary').focus(); }
        }}><summary>On this page <span>{post.headings.find(heading => heading.id === active)?.title || 'Choose a section'}</span></summary><nav aria-label="Table of contents">{contents}</nav></details>}
        {post && <div className="blog-progress" style={{ transform: `scaleX(${progress})` }} aria-hidden="true" />}
    </div>;
}
